import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {useAuth} from '../../Auth/AuthContext'; 

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/universidades`;


const EditarUniversidad = ({ abrirModal, cerrarModal, universidadId, onUniversidadEditada }) => {
    const {getToken} = useAuth(); 
    const [formData, setFormData] = useState({
        nombre: '',
        pagina: '',
        direccion: '',
        telefonos: [''],
        correos: [''],
        locacion: '',
        tipo: '',
        mapEmbed: '',
    });
    
    const [fileData, setFileData] = useState({
        logoFile: null,
        previewLogo: null,
        removeLogo: false,
    });
    
    const [tiposUniversidad, setTiposUniversidad] = useState([]);
    const [plantelId, setPlantelId] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errores, setErrores] = useState({
            pagina: '',
            correos: [],
            telefonos: [],
            mapEmbed: '',
            logo: '',
            tipo: '',
    });

    const token = getToken(); 

    // Función para extraer URL de Google Maps del iframe
    const extractGoogleMapsUrl = useCallback((input) => {
        if (!input || typeof input !== 'string') return input;
        
        // Buscar el patrón src="URL" en el iframe
        const srcMatch = input.match(/src=["']([^"']+)["']/i);
        if (srcMatch && srcMatch[1]) {
            return srcMatch[1];
        }
        
        // Si no es un iframe, devolver el input original
        return input;
    }, []);

    const validators = useMemo(() => ({
        url: (url) => {
            if (!url) return '';
            const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
            return !urlRegex.test(url) ? 'La URL no es válida.' : '';
        },
        
        email: (email) => {
            if (!email) return '';
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            return !emailRegex.test(email) ? 'El correo no es válido.' : '';
        },
        
        telefono: (telefono) => {
            if (!telefono) return '';
            return (telefono.length < 10 || telefono.length > 15) 
                ? 'El número de teléfono debe tener entre 10 y 15 dígitos.' 
                : '';
        },
        
        validarMapEmbed: (embedUrl) => {
            if (!embedUrl) return '';
            // Extraer la URL si viene en formato iframe
            const url = extractGoogleMapsUrl(embedUrl);
            return (!url.includes('google.com/maps/embed') && !url.includes('maps.google.com/maps'))
                ? 'Debe ser una URL de Google Maps embed válida.'
                : '';
        }
    }), [extractGoogleMapsUrl]);

    const parseArrayFromDB = useCallback((value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed.filter(item => item && item.trim() !== '');
            }
            return [String(parsed)];
        } catch (e) {
            if (typeof value === 'string') {
                if (value.includes(',')) { 
                    return value.split(',').map(item => item.trim()).filter(item => item !== ''); 
                }
                const trimmed = value.trim();
                return trimmed ? [trimmed] : [];
            }
            return [];
        }
    }, []);

    const limpiarFormulario = useCallback(() => {
        setFormData({
            nombre: '',
            pagina: '',
            direccion: '',
            telefonos: [''],
            correos: [''],
            locacion: '',
            tipo: '',
            mapEmbed: '',
        });
        setFileData({
            logoFile: null,
            previewLogo: null,
            removeLogo: false,
        });
        setPlantelId(null);
        setErrores({
            pagina: '', 
            correos: [], 
            telefonos: [], 
            mapEmbed: '', 
            logo: '', 
            tipo: '', 
        });
    }, []);

    useEffect(() => {
        const cargarTipos = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/obtener-tipos-universidades`);
                if (!response.ok) throw new Error('Error al cargar tipos de universidad');
                const data = await response.json();
                setTiposUniversidad(data);
            } catch (error) {
                console.error('Error al cargar tipos de universidades:', error);
            }
        };
        cargarTipos();
    }, []);

    useEffect(() => {
        if (!universidadId || !abrirModal) return;

        const cargarDatosUniversidad = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/obtener-universidad/${universidadId}`, { 
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`Error ${response.status}: ${response.statusText}`);
                }
                const data = await response.json();
                setFormData({
                    nombre: data.universidad?.un_nombre || '',
                    pagina: data.universidad?.un_pagina || '',
                    tipo: String(data.universidad?.un_tipo || ''),
                    direccion: data.plantel?.pl_direccion || '',
                    locacion: '',
                    mapEmbed: data.plantel?.pl_locacion || '',
                    telefonos: data.plantel ? 
                        (parseArrayFromDB(data.plantel.pl_numero).length > 0 ? 
                            parseArrayFromDB(data.plantel.pl_numero) : ['']) : [''],
                    correos: data.plantel ? 
                        (parseArrayFromDB(data.plantel.pl_correo).length > 0 ? 
                            parseArrayFromDB(data.plantel.pl_correo) : ['']) : [''],
                });

                setFileData(prev => ({
                    ...prev,
                    previewLogo: data.universidad?.un_logo 
                        ? `${API_BASE_URL}/logos-universidades/${data.universidad.un_logo}`
                        : null
                }));

                setPlantelId(data.plantel?.pl_clave || null);
                
            } catch (error) {
                console.error('Error al obtener datos de la universidad:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar los datos de la universidad.',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });
            } finally {
                setLoading(false);
            }
        };

        cargarDatosUniversidad();
    }, [universidadId, abrirModal, parseArrayFromDB]);

    useEffect(() => {
        if (!abrirModal && !isClosing) {
            limpiarFormulario();
        }
    }, [abrirModal, isClosing, limpiarFormulario]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleLogoChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'image/png') {
            setErrores(prev => ({ ...prev, logo: 'Solo se permiten archivos PNG.' }));
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            setErrores(prev => ({ ...prev, logo: 'El archivo no debe superar los 5MB.' }));
            return;
        }

        setFileData(prev => ({ ...prev, logoFile: file, removeLogo: false }));
        setErrores(prev => ({ ...prev, logo: '' }));
        
        const reader = new FileReader();
        reader.onload = (e) => setFileData(prev => ({ ...prev, previewLogo: e.target.result }));
        reader.readAsDataURL(file);
    }, []);

    const eliminarLogo = useCallback(() => {
        setFileData({ logoFile: null, previewLogo: null, removeLogo: true });
        const fileInput = document.getElementById('logo');
        if (fileInput) fileInput.value = '';
    }, []);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'pagina') {
            setErrores(prev => ({ ...prev, pagina: validators.url(value) }));
        } else if (field === 'mapEmbed') {
            setErrores(prev => ({ ...prev, mapEmbed: validators.validarMapEmbed(value) }));
        }
    }, [validators]);

    // Manejador específico para mapEmbed que extrae automáticamente la URL
    const handleMapEmbedChange = useCallback((e) => {
        let value = e.target.value;
        
        // Extraer automáticamente la URL si es un iframe
        value = extractGoogleMapsUrl(value);
        
        handleInputChange('mapEmbed', value);
    }, [handleInputChange, extractGoogleMapsUrl]);

    const handleTelefonoChange = useCallback((index, value) => {
        const nuevosTelefonos = [...formData.telefonos];
        nuevosTelefonos[index] = value;
        setFormData(prev => ({ ...prev, telefonos: nuevosTelefonos }));

        const nuevosErroresTelefonos = [...errores.telefonos];
        nuevosErroresTelefonos[index] = validators.telefono(value);
        setErrores(prev => ({ ...prev, telefonos: nuevosErroresTelefonos }));
    }, [formData.telefonos, errores.telefonos, validators]);

    const handleCorreoChange = useCallback((index, valor) => {
        const nuevosCorreos = [...formData.correos];
        nuevosCorreos[index] = valor;
        setFormData(prev => ({ ...prev, correos: nuevosCorreos }));

        const nuevosErroresCorreos = [...errores.correos];
        nuevosErroresCorreos[index] = validators.email(valor);
        setErrores(prev => ({ ...prev, correos: nuevosErroresCorreos }));
    }, [formData.correos, errores.correos, validators]);

    const agregarTelefono = useCallback(() => {
        setFormData(prev => ({ ...prev, telefonos: [...prev.telefonos, ''] }));
        setErrores(prev => ({ ...prev, telefonos: [...prev.telefonos, ''] }));
    }, []);

    const eliminarTelefono = useCallback((index) => {
        if (formData.telefonos.length > 1) {
            setFormData(prev => ({ 
                ...prev, 
                telefonos: prev.telefonos.filter((_, i) => i !== index) 
            }));
            setErrores(prev => ({
                ...prev, 
                telefonos: prev.telefonos.filter((_, i) => i !== index)
            }));
        }
    }, [formData.telefonos.length]);

    const agregarCorreo = useCallback(() => {
        setFormData(prev => ({ ...prev, correos: [...prev.correos, ''] }));
        setErrores(prev => ({ ...prev, correos: [...prev.correos, ''] }));
    }, []);

    const eliminarCorreo = useCallback((index) => {
        if (formData.correos.length > 1) {
            setFormData(prev => ({ 
                ...prev, 
                correos: prev.correos.filter((_, i) => i !== index) 
            }));
            setErrores(prev => ({
                ...prev,
                correos: prev.correos.filter((_, i) => i !== index)
            }));
        }
    }, [formData.correos.length]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) setIsClosing(true);
    }, []);

    const handleCloseModal = useCallback(() => setIsClosing(true), []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        const nuevosErrores = { 
            pagina: '', 
            correos: [], 
            telefonos: [], 
            mapEmbed: '', 
            logo: '', 
            tipo: '', 
        }; 
        let hayErrores = false;

        // Validaciones
        if (!formData.tipo) {
            nuevosErrores.tipo = 'Debes seleccionar un tipo de universidad.';
            hayErrores = true;
        }

        if (formData.pagina.trim() && validators.url(formData.pagina)) {
            nuevosErrores.pagina = validators.url(formData.pagina);
            hayErrores = true;
        }

        formData.correos.forEach((correo, index) => {
            if (correo.trim() && validators.email(correo)) {
                nuevosErrores.correos[index] = validators.email(correo);
                hayErrores = true;
            }
        });

        formData.telefonos.forEach((telefono, index) => {
            if (telefono.trim() && validators.telefono(telefono)) {
                nuevosErrores.telefonos[index] = validators.telefono(telefono);
                hayErrores = true;
            }
        });

        if (formData.mapEmbed.trim() && validators.validarMapEmbed(formData.mapEmbed)) {
            nuevosErrores.mapEmbed = validators.validarMapEmbed(formData.mapEmbed);
            hayErrores = true;
        }

        setErrores(nuevosErrores);

        if (hayErrores) return;

        try {
            const telefonosFiltrados = formData.telefonos
                .filter(tel => tel && tel.trim() !== '')
                .map(tel => tel.replace(/\D/g, ''));
            const correosFiltrados = formData.correos.filter(correo => correo && correo.trim() !== '');
            
            const formDataToSend = new FormData();
            formDataToSend.append('nombre', formData.nombre.trim());
            if (formData.pagina.trim()) formDataToSend.append('pagina', formData.pagina.trim());
            if (formData.direccion.trim()) formDataToSend.append('direccion', formData.direccion.trim());
            if (formData.mapEmbed.trim()) formDataToSend.append('locacion', formData.mapEmbed.trim());
            
            if (telefonosFiltrados.length > 0) {
                formDataToSend.append('telefonos', JSON.stringify(telefonosFiltrados));
            }
            if (correosFiltrados.length > 0) {
                formDataToSend.append('correos', JSON.stringify(correosFiltrados));
            }
            
            if (fileData.logoFile) {
                formDataToSend.append('logo', fileData.logoFile);
            } else if (fileData.removeLogo) {
                formDataToSend.append('removeLogo', 'true');
            }
            
            if (plantelId) {
                formDataToSend.append('plantelId', plantelId);
            }

            formDataToSend.append('tipo', formData.tipo);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/actualizar-universidad/${universidadId}`, {
                method: 'POST',
                body: formDataToSend,
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Error al actualizar universidad');
            }

            await Swal.fire({
                title: 'Universidad actualizada correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' },
            });

            handleCloseModal();
            if (onUniversidadEditada) {
                await onUniversidadEditada();
            }

        } catch (error) {
            console.error('Error al actualizar universidad:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'Ha ocurrido un error al actualizar la universidad.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' },
            });
        }
    }, [formData, fileData, plantelId, universidadId, validators, handleCloseModal]);

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalAniadirCont} ${abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''}`}>
                <div className={styles.modalScrollableAniadir}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Editar universidad</h2>
                    
                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.loadingSpinner}></div>
                            <p>Cargando datos de la universidad...</p>
                        </div>
                    ) : (
                        <form className={styles.formUsuario} onSubmit={handleSubmit}>
                            <label htmlFor="nombre">
                                Nombre de la universidad: *
                                <input
                                    type="text"
                                    id="nombre"
                                    value={formData.nombre}
                                    placeholder="Ingresa el nombre de la universidad"
                                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                                    required
                                />
                            </label>

                            <label htmlFor="tipo">
                                Tipo de universidad: *
                                <select
                                    id="tipo"
                                    value={formData.tipo}
                                    onChange={e => handleInputChange('tipo', e.target.value)}
                                    required
                                >
                                    <option value="">Selecciona un tipo</option>
                                    {tiposUniversidad.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.nombre}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {errores.tipo && <p className={styles.mensajeErrorAniadir}>{errores.tipo}</p>}

                            {fileData.previewLogo && (
                                <div className={styles.previewContainer}>
                                    <p>Logo actual:</p>
                                    <div className={styles.logoPreview}>
                                        <img
                                            src={fileData.previewLogo}
                                            alt="Logo de la universidad"
                                            className={styles.logoImage}
                                        />
                                        <button
                                            type="button"
                                            onClick={eliminarLogo}
                                            className={styles.botonEliminarLogo}
                                            title="Eliminar logo"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}

                            <label htmlFor="logo">
                                {fileData.previewLogo ? 'Cambiar logo (PNG, máx. 5MB):' : 'Subir nuevo logo (PNG, máx. 5MB): '}
                                <input
                                    type="file"
                                    id="logo"
                                    accept=".png"
                                    onChange={handleLogoChange}
                                />
                            </label>
                            {errores.logo && <p className={styles.mensajeErrorAniadir}>{errores.logo}</p>}

                            <label htmlFor="pagina">
                                Página web (opcional):
                                <input
                                    type="url"
                                    id="pagina"
                                    value={formData.pagina}
                                    placeholder="https://www.universidad.edu.mx"
                                    onChange={(e) => handleInputChange('pagina', e.target.value)}
                                />
                            </label>
                            {errores.pagina && <p className={styles.mensajeErrorAniadir}>{errores.pagina}</p>}

                            <h3 className={styles.tituloAniadir}>Información del plantel</h3>

                            <label htmlFor="direccion">
                                Dirección:
                                <input
                                    type="text"
                                    id="direccion"
                                    value={formData.direccion}
                                    placeholder="Calle, colonia, ciudad"
                                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                                />
                            </label>

                            <div className={styles.seccionTelefonos}>
                                <label>Números de teléfono:</label>
                                {formData.telefonos.map((telefono, index) => (
                                    <div key={index}>
                                        <div className={styles.inputGroup}>
                                        <div className={styles.inputWrapper}>
                                            <PhoneInput
                                                country={'mx'}
                                                value={telefono}
                                                onChange={(value) => handleTelefonoChange(index, value)}
                                                disabled={loading}
                                                inputClass={styles.inputTelefono}
                                                containerClass={styles.phoneInputContainer}
                                                placeholder="Ingresa un teléfono de contacto"
                                                preferredCountries={['mx', 'us', 'ca']}
                                                enableSearch={true}
                                                searchPlaceholder="Buscar país..."
                                                inputProps={{
                                                    'aria-label': `Teléfono de contacto ${index + 1}`,
                                                }}
                                            />
                                            {formData.telefonos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => eliminarTelefono(index)}
                                                    className={styles.botonEliminar}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        </div>
                                        {errores.telefonos[index] && (
                                            <p className={styles.mensajeErrorAniadir}>{errores.telefonos[index]}</p>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={agregarTelefono}
                                    className={styles.botonAgregar}
                                >
                                    + Agregar teléfono
                                </button>
                            </div>

                            <div className={styles.seccionCorreos}>
                                <label>Correos de contacto:</label>
                                {formData.correos.map((correo, index) => (
                                    <div key={index}>
                                        <div className={styles.inputGroup}>
                                            <input
                                                type="email"
                                                value={correo}
                                                placeholder="contacto@universidad.edu.mx"
                                                onChange={(e) => handleCorreoChange(index, e.target.value)}
                                                className={styles['text-input']}
                                            />
                                            {formData.correos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => eliminarCorreo(index)}
                                                    className={styles.botonEliminar}
                                                >
                                                    ×
                                                </button>
                                            )}
                                            </div>
                                        {errores.correos[index] && (
                                            <p className={styles.mensajeErrorAniadir}>{errores.correos[index]}</p>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={agregarCorreo}
                                    className={styles.botonAgregar}
                                >
                                    + Agregar correo
                                </button>
                            </div>

                            <label htmlFor="mapEmbed">
                                URL de Google Maps Embed:
                                <small className={styles.helpText}>
                                    Opcional - Para obtener la URL: Ve a Google Maps → Busca la ubicación → Compartir → Insertar un mapa → Copia la URL del src o pega directamente el código iframe
                                </small>
                                <input
                                    type="url"
                                    id="mapEmbed"
                                    value={formData.mapEmbed}
                                    placeholder="Ej: https://www.google.com/maps/embed?pb=... o pega directamente el código iframe"
                                    onChange={handleMapEmbedChange}
                                />
                            </label>
                            {errores.mapEmbed && <p className={styles.mensajeErrorAniadir}>{errores.mapEmbed}</p>}

                            <button type="submit" className={styles.botonGuard}>
                                Actualizar Universidad
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditarUniversidad;